import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {Observable, catchError, of, Subject} from 'rxjs';
import { TeamService } from '../teams/team.service';
import {takeUntil} from "rxjs/operators";
import { LogoComponent, logoUrlClub } from 'app/components/logo/logo.component';

interface Player {
    id: string;
    name: string;
    position: string;
    number: number;
    avatar?: string;
}

interface Match {
    id: string;
    opponent: string;
    opponentLogo_url: string;
    date: string;
    goals: number;
    opponent_goals: number;
    homeTeam: string;
    awayTeam: string;
    isHome: boolean;
    status: string; // Keep as string to handle 'forfeit' or other statuses
    location: string;
    type: string;
}

interface upcomingEvents {
    teamLogo_url: "/assets/images/logo/rcfmb-logo.png";
    opponentLogo_url: any;
    type: string;
    id: string;
    opponent: string;
    date: Date;
    location: string;
    homeTeam: string;
    awayTeam: string;
    isHome: boolean;
}

interface Coach {
    id: string;
    name: string;
    title: string;
    avatar: string;
    description: string;
}

interface TeamData {
    upcomingEvents: any[];
    teamInfo: {
        banner_url?: string;
        id: string;
        name: string;
        description: string;
        location: string;
        coach: string;
        email: string;
        phone: string;
        season: string;
        logo_url?: string;
    };
    players: Player[];
    coaches: Coach[];
    pastMatches: Match[];
    upcomingMatches: upcomingEvents[];
    nextMatch: upcomingEvents | null;
}

@Component({
    selector: 'app-team-public-view',
    standalone: true,
    imports: [CommonModule, LogoComponent],
    templateUrl: './team-public-view.component.html',
    styleUrl: './team-public-view.component.scss'
})
export class TeamPublicViewComponent implements OnInit, OnDestroy {

    private route = inject(ActivatedRoute);
    private teamService = inject(TeamService);
    private _destroyed = new Subject<void>();
    compositionAvailable: boolean = false;
    team: TeamData | null = null;
    loading = true;
    error = false;
    countdown: { days: number; hours: number; minutes: number; seconds: number } = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    countdownInterval?: any;
    currentCoachIndex = 0;
    readonly teamLogoUrl: string = logoUrlClub;
    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntil(this._destroyed)).subscribe(params => {
            const teamId = params.get('id');
            if (teamId) {
                this.loadTeam(teamId);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    }

    private loadTeam(teamId: string): void {
        this.loading = true;
        this.teamService.getTeamPublicData(teamId).subscribe({
            next: (data: TeamData) => {
                console.log('Team Data:', data);
                this.team = {
                    ...data,
                    pastMatches: data.pastMatches.map(match => ({
                        ...match,
                    })),
                };
                this.loading = false;
                if (data.nextMatch) {
                    this.startCountdown();
                }
            },
            error: (err) => {
                console.error('Error loading team data:', err);
                this.error = true;
                this.loading = false;
            }
        });
    }

    private startCountdown(): void {
        if (!this.team?.nextMatch) return;

        this.updateCountdown();
        this.countdownInterval = setInterval(() => {
            this.updateCountdown();
        }, 1000);
    }

    private updateCountdown(): void {
        if (!this.team?.nextMatch) return;

        const now = new Date().getTime();
        const matchTime = new Date(this.team.nextMatch.date).getTime();
        const difference = matchTime - now;

        if (difference > 0) {
            this.countdown.days = Math.floor(difference / (1000 * 60 * 60 * 24));
            this.countdown.hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            this.countdown.minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            this.countdown.seconds = Math.floor((difference % (1000 * 60)) / 1000);
        } else {
            this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
    }

    getCurrentCoach(): Coach {
        return this.team?.coaches[this.currentCoachIndex] || { id: '', name: 'No coach', title: '', avatar: '', description: '' };
    }

    nextCoach(): void {
        if (this.team?.coaches) {
            this.currentCoachIndex = (this.currentCoachIndex + 1) % this.team.coaches.length;
        }
    }

    previousCoach(): void {
        if (this.team?.coaches) {
            this.currentCoachIndex = this.currentCoachIndex === 0 ? this.team.coaches.length - 1 : this.currentCoachIndex - 1;
        }
    }

    goToCoach(index: number): void {
        this.currentCoachIndex = index;
    }

    formatCountdownNumber(num: number): string {
        return num.toString().padStart(2, '0');
    }

    get upcomingEvents(): upcomingEvents[] {
        return this.team?.upcomingEvents || [];
    }
    get pastMatches(): Match[] {
        return this.team?.pastMatches || [];
    }
    get players(): Player[] {
        if (this.coaches?.length && this.team?.players?.length) {
            const coachIds = this.coaches.map((coach) => coach.id);
            return this.team?.players.filter((player) => !coachIds.includes(player.id));
        }
        return this.team?.players || [];
    }

    get coaches(): Coach[] {
        return this.team?.coaches || [];
    }

    get upcomingMatch(): upcomingEvents | null {
        return this.team?.nextMatch || null;
    }
    getEventTypeLabel(type: string): string {
        const typeLabels = {
            MATCH_AMICAL: "Match amical",
            MATCH_ENTRE_NOUS: "Match interne",
            MATH_DE_COUPE: "Match de coupe",
            MATCH_CHAMPIONNAT: "Match de championnat",
            TOURNOI: "Tournoi",
            ENTRAINEMENT: "Entraînement",
            EVENEMENT: "Événement",
        };

        return typeLabels[type] || type;
    }
    formatPlayerName(fullName: string): string {
        const [firstName, lastName] = fullName.split(' ');
        return `${firstName} ${lastName ? lastName.charAt(0) + '.' : ''}`;
    }
    getPlayerInitials(fullName: string): string {
        const [firstName, lastName] = fullName.split(' ');
        return `${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ''}`.toUpperCase();
    }

    formatMatchScore(match: Match): string {
        const homeGoals = match.goals ?? '';
        const awayGoals = match.opponent_goals ?? '';
        return `${homeGoals} - ${awayGoals}`;
    }

    getMatchResult(match: Match): string {
        if (match.status === 'forfeit') {
            return 'forfeit';
        }
        if (match.goals > match.opponent_goals) {
            return 'win';
        } else if (match.goals < match.opponent_goals) {
            return 'loss';
        } else {
            return 'draw';
        }
    }
}
